import React, { useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity, Platform } from "react-native";
import FontFamily from "../../../constants/fonts";
import LhActionPlanButtonGroup from "../../../components/LhActionPlanButtonGroup";
import { getActionPlanState, shouldAllowToggleInteraction } from "../../../utils/actionPlanUtils";
import { formatDate } from "../../../utils/dateFormatter";
import { useTheme } from "../../../theme";

interface SiteEvalCardProps {
  item: any;
  columns: any[];
  onRowClick: (rowData: any, colIdx?: number) => void;
  onToggleChange?: (id: number, value: boolean) => void;
  activeItemsData?: any[];
  isActiveItemsTable?: boolean;
}

const SiteEvalCard: React.FC<SiteEvalCardProps> = ({
  item,
  columns,
  onRowClick,
  onToggleChange,
  activeItemsData = [],
  isActiveItemsTable = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [isExpanded, setIsExpanded] = useState(false);

  // Get display name (Site Evaluator Name)
  const getDisplayName = (): string => {
    return item.CreatedBy || item.SiteEvaluatorName || item.Name || item.Site || item.details || item.title || "Site Evaluation";
  };

  // Get site location
  const getSiteLocation = (): string => {
    return item.Location || item.location || item.Site || "N/A";
  };

  // Get date/time (Finalized Time)
  const getDateTime = (): string => {
    const raw = item.Modified || item.modified || item.finalizedDate || item.FinalizedDate || item.CreatedDate || item.createdDate || item.Date || item.date;
    if (!raw) return "-";
    try {
      return formatDate(raw);
    } catch {
      return String(raw);
    }
  };

  // Get period/week
  const getPeriodWeek = (): string => {
    const period = item.Period || "";
    const week = item.Week || "";
    if (period && week) {
      return `P${period}W${week}`;
    } else if (period) {
      return `P${period}`;
    } else if (week) {
      return `W${week}`;
    }
    return item.PeriodWeek || "-";
  };

  // Get assigned supervisor name
  const getAssignedSupervisor = (): string => {
    return item.AssignedSupervisor || item.assignedSupervisor || item.Supervisor || item.supervisor || item.SupervisorName || item.supervisorName || "-";
  };

  // Get the status/action tag style based on status value
  const getStatusTagStyle = (status: string | undefined) => {
    if (!status) {
      return styles.statusTagDefault;
    }
    const statusLower = String(status).toLowerCase().trim().replace(/[_-]+/g, " ");
    // Map common server values to tag styles used in LHDataTable
    if (statusLower === "completed" || statusLower === "complete") return styles.statusTagCompleted;
    if (statusLower === "active") return styles.statusTagActive;
    if (statusLower === "failed" || statusLower === "error") return styles.statusTagFailed;
    // Treat in-progress / pending / inprogress as in-progress
    if (statusLower === "in progress" || statusLower === "pending" || statusLower === "inprogress") return styles.statusTagInProgress;
    // Fallback to default
    return styles.statusTagDefault;
  };

  // Get status value
  const getStatus = (): string => {
    const raw = item.Status || item.status || item.ActionPlanStatus || item.actionPlanStatus || item.action || "New";
    // Normalize display: capitalize first letter of each word and map some variants
    const s = String(raw || "").toLowerCase().trim().replace(/[_-]+/g, " ");
    if (!s) return "New";
    if (s === "inprogress") return "In Progress";
    if (s === "in-progress" || s === "in progress" || s === "pending") return "In Progress";
    if (s === "completed" || s === "complete") return "Completed";
    if (s === "active") return "Active";
    // Capitalize each word
    return s
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  // Get count of action plans
  const getCountOfActionPlans = (): number => {
    return item.CountOfActionPlans || item.countOfActionPlans || item.count || 0;
  };

  const displayName = getDisplayName();
  const siteLocation = getSiteLocation();
  const dateTime = getDateTime();
  const assignedSupervisor = getAssignedSupervisor();
  const periodWeek = getPeriodWeek();
  const status = getStatus();
  const countOfActionPlans = getCountOfActionPlans();

  // Get action plan state and interaction allowance
  const actionPlanState = getActionPlanState(item, activeItemsData);
  const canInteract = shouldAllowToggleInteraction(item, activeItemsData, isActiveItemsTable);
  
  // Map the state to button status
  const buttonStatus = actionPlanState === "completed" ? "completed" : "incomplete";

  return (
    <View style={[styles.cardContainer, isDark && { backgroundColor: colors.background.elevated, shadowColor: "transparent" }]}>
      {/* Tap overlay - clicking anywhere opens overlay */}
      <TouchableOpacity
        style={styles.cardTapOverlay}
        onPress={() => onRowClick(item)}
        activeOpacity={0.05}
      />

      {/* Card Header: Title + Status Badge */}
      <View style={[styles.cardHeader, { zIndex: 10 }]}>
        <TouchableOpacity
          onPress={() => onRowClick(item, 0)}
          style={[styles.cardTitleContainer, { zIndex: 11 }]}
          activeOpacity={0.7}
        >
          <Text style={[styles.cardTitle, isDark && { color: colors.brand.primary }]} numberOfLines={2}>
            {displayName}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => onRowClick(item, 0)}
          style={[styles.statusBadge, getStatusTagStyle(status), { zIndex: 11 }]}
          activeOpacity={0.7}
        >
          <Text style={styles.statusBadgeText}>{status}</Text>
        </TouchableOpacity>
      </View>

      {/* Site Location */}
      {/* Show Site only for non-active-items view to match web Active Items columns */}
      {!isActiveItemsTable && (
        <View style={styles.cardInfo}>
          <Text style={[styles.infoLabel, isDark && { color: colors.text.secondary }]}>Site:</Text>
          <Text style={[styles.infoValue, isDark && { color: colors.text.primary }]} numberOfLines={0}>{siteLocation}</Text>
        </View>
      )}

      {/* Show Assigned Supervisor only for Active Items, otherwise show Finalized Time */}
      {isActiveItemsTable ? (
        <View style={styles.cardInfo}>
          <Text style={[styles.infoLabel, isDark && { color: colors.text.secondary }]}>Assigned Supervisor:</Text>
          <Text style={[styles.infoValue, isDark && { color: colors.text.primary }]} numberOfLines={0}>{assignedSupervisor}</Text>
        </View>
      ) : (
        <View style={styles.cardInfo}>
          <Text style={[styles.infoLabel, isDark && { color: colors.text.secondary }]}>Finalized Time:</Text>
          <Text style={[styles.infoValue, isDark && { color: colors.text.primary }]} numberOfLines={0}>{dateTime}</Text>
        </View>
      )}

      {/* Period/Week - shown for both views */}
      <View style={styles.cardInfo}>
        <Text style={[styles.infoLabel, isDark && { color: colors.text.secondary }]}>Period Week:</Text>
        <Text style={[styles.periodWeek, isDark && { color: colors.text.primary }]}>{periodWeek}</Text>
      </View>

      {/* Non-active view shows Count of Action Plans */}
      {!isActiveItemsTable && (
        <View style={styles.cardInfo}>
          <Text style={[styles.infoLabel, isDark && { color: colors.text.secondary }]}>Count of Action Plans:</Text>
          <Text style={[styles.infoValue, isDark && { color: colors.text.primary }]} numberOfLines={0}>{countOfActionPlans}</Text>
        </View>
      )}

      {/* Action Plan Status - Button Group (no label, left-aligned) */}
      <View style={styles.actionPlanStatusContainer}>
        <LhActionPlanButtonGroup
          status={buttonStatus}
          onStatusChange={(newStatus) => {
            // Convert button status back to boolean toggle value
            const newValue = newStatus === "completed";
            onToggleChange && onToggleChange(item.id || item.Id, newValue);
          }}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    marginHorizontal: 12,
    marginBottom: 12,
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow: "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
      },
    }),
    position: "relative",
  },
  
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
    gap: 8,
    flexWrap: "nowrap",
  },

  cardTitleContainer: {
    flex: 1,
    paddingRight: 8,
  },

  cardTitle: {
    fontSize: 15,
    fontWeight: "600",
    color: "#005CBC",
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },

  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    minWidth: 60,
    alignItems: "center",
    justifyContent: "center",
  },

  statusBadgeText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#000000",
    textAlign: "center",
    lineHeight: 16,
    fontFamily: FontFamily.regular,
  },

  statusTagCompleted: {
    backgroundColor: "#027C06",
  },

  statusTagInProgress: {
    backgroundColor: "#E5A408",
  },

  statusTagActive: {
    backgroundColor: "#005CBC",
  },

  statusTagFailed: {
    backgroundColor: "#DC3545",
  },

  statusTagDefault: {
    backgroundColor: "#6C757D",
  },

  cardInfo: {
    marginBottom: 12,
    flexDirection: "row",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },

  infoLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: "#616161",
    marginRight: 4,
    fontFamily: FontFamily.regular,
    flexShrink: 0,
  },

  infoValue: {
    fontSize: 13,
    fontWeight: "400",
    color: "#616161",
    lineHeight: 18,
    fontFamily: FontFamily.regular,
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },

  periodWeek: {
    fontSize: 13,
    fontWeight: "400",
    color: "#616161",
    textAlign: "left",
    lineHeight: 18,
    fontFamily: FontFamily.regular,
    flex: 1,
  },

  cardTapOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 1,
    borderRadius: 8,
  },

  actionPlanStatusContainer: {
    marginTop: 12,
    paddingTop: 0,
    alignItems: "flex-start",
  },
});

export default SiteEvalCard;
